import { FieldShell } from '@atlas/ui-components'
import { Button, IconButton, Tag } from '@atlas/ui-primitives'
import { X } from 'lucide-react'
import { useId, useState } from 'react'
import { describeToken } from './ColumnMapTable'

export interface TokenListFieldProps {
	label: string
	tokens: string[]
	onChange: (tokens: string[]) => void
	help: string
	/** A refusal to show in place of the help. */
	error?: string
	/** Keep a typed space or tab as the token, rather than trimming it away. */
	keepWhitespace?: boolean
	/** A token that cannot be typed, such as a line break, added with one press. */
	quickAdd?: { label: string; token: string }
	/** Shown when Enter is pressed on nothing; without it, Enter on nothing does nothing. */
	emptyMessage?: string
}

/** A short list of strings, added by typing and Enter and removed one by one. */
export function TokenListField({
	label,
	tokens,
	onChange,
	help,
	error,
	keepWhitespace,
	quickAdd,
	emptyMessage,
}: TokenListFieldProps) {
	const id = useId()
	const [text, setText] = useState('')
	const [refusal, setRefusal] = useState<string>()

	const add = (token: string) => {
		if (!tokens.includes(token)) onChange([...tokens, token])
		setText('')
		setRefusal(undefined)
	}
	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Enter') return
		event.preventDefault()
		const token = keepWhitespace ? text : text.trim()
		if (token) add(token)
		else if (emptyMessage) setRefusal(emptyMessage)
	}
	const message = refusal ?? error

	return (
		<FieldShell label={label} htmlFor={id} error={message} description={help}>
			<div className="flex flex-wrap items-center gap-ds-1 min-h-8 rounded-ds-md border border-ds-border px-ds-2 py-0.5">
				{tokens.map((token, i) => (
					<Tag key={token} variant="muted" size="sm" className="gap-ds-1">
						<span className="font-ds-mono">{describeToken(token)}</span>
						<IconButton
							variant="muted"
							size="sm"
							className="h-4 w-4 border-0 bg-transparent"
							aria-label={`Remove ${describeToken(token)}`}
							onPress={() => onChange(tokens.filter((_, j) => j !== i))}
						>
							<X aria-hidden className="h-3 w-3" />
						</IconButton>
					</Tag>
				))}
				<input
					id={id}
					value={text}
					placeholder="Type and press Enter"
					onChange={(e) => setText(e.target.value)}
					onKeyDown={onKeyDown}
					aria-describedby={message ? `${id}-error` : `${id}-description`}
					className="min-w-24 flex-1 bg-transparent py-ds-1 text-ds-sm text-ds-text outline-none"
				/>
				{quickAdd && !tokens.includes(quickAdd.token) && (
					<Button variant="secondary" size="sm" onPress={() => add(quickAdd.token)}>
						{quickAdd.label}
					</Button>
				)}
			</div>
		</FieldShell>
	)
}
